import { BoundaryItem, GrowthDataItem } from "./useSimulationModelsBoundaries";
import { Box, BoxProps, Theme, Typography, useTheme } from "@mui/material";
import { ComponentProps, useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { formatNumber } from "@app/utils";
import {
  ChartTooltip,
  RenderTooltip,
  useChartTooltip,
} from "@app/components/ChartTooltip";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { ChartDataset, ChartTypeRegistry } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { CarChartLegend } from "@app/components/ChartLegend";

type Options = ComponentProps<typeof Chart>["options"];

interface SimulationModelsBoundariesGrowthDataPoint {
  x: number;
  y: number;
}

interface SimulationModelsBoundariesGrowthData {
  xLabel: string;
  yLabel: string;
  datasets: ChartDataset<"line", SimulationModelsBoundariesGrowthDataPoint[]>[];
}

export const getSimulationModelsBoundariesGrowthData = ({
  theme,
  items,
  forPdf,
}: {
  theme: Theme;
  items: GrowthDataItem[];
  forPdf?: boolean;
}): SimulationModelsBoundariesGrowthData => {
  const upperGrowthColor = theme.palette.chartsColor.orange;
  const lowerGrowthColor = theme.palette.chartsColor.green;

  const result: SimulationModelsBoundariesGrowthData = {
    datasets: [
      {
        type: "line",
        id: "upper_growth",
        label: "Upper Growth",
        data: items.map((i) => ({
          x: i.year,
          y: i.upperDollarGrowth ?? 0,
        })),
        borderColor: upperGrowthColor,
        pointBorderColor: upperGrowthColor,
        pointBackgroundColor: upperGrowthColor,
        pointBorderWidth: 1,
        pointRadius: forPdf ? 1 : 3,
        borderWidth: 1,
      },
      {
        type: "line",
        id: "lower_growth",
        label: "Lower Growth",
        data: items.map((i) => ({
          x: i.year,
          y: i.lowerDollarGrowth ?? 0,
        })),
        borderColor: lowerGrowthColor,
        pointBorderColor: lowerGrowthColor,
        pointBackgroundColor: lowerGrowthColor,
        pointBorderWidth: 1,
        pointRadius: forPdf ? 1 : 3,
        borderWidth: 1,
        hoverBorderColor: "black",
        hoverBackgroundColor: "black",
      },
    ],
    xLabel: "Boundary Year",
    yLabel: "Dollar Growth",
  };

  return result;
};

interface SimulationModelsBoundariesGrowthDataChartProps {
  sx?: BoxProps["sx"];
  items: GrowthDataItem[];
  isWhiteContext?: boolean;
}

export const SimulationModelsBoundariesGrowthDataChart = ({
  sx,
  items,
  isWhiteContext,
}: SimulationModelsBoundariesGrowthDataChartProps) => {
  const theme = useTheme();

  const { datasets, yLabel, xLabel } = useMemo(
    () =>
      getSimulationModelsBoundariesGrowthData({
        theme,
        items,
      }),
    [theme, items],
  );

  const { tooltipPlugin, tooltipData } = useChartTooltip({
    chartName: "SimulationModelsBoundariesGrowthDataChart",
  });

  const formatXValue = (value: number | string) =>
    typeof value === "number" ? formatNumber(value, { decimalPlaces: 2 }) : "";

  const formatYValue = (value: number | string) =>
    typeof value === "number" ? formatNumber(value, { decimalPlaces: 2 }) : "";

  const chartRef = useRef<ChartJSOrUndefined<
    keyof ChartTypeRegistry,
    SimulationModelsBoundariesGrowthDataPoint[],
    unknown
  > | null>();

  const renderTooltip: RenderTooltip = (datasetIndex, dataIndex) => {
    const ds = datasets[datasetIndex];

    const xValue = `${xLabel}: ${formatXValue(ds?.data?.[dataIndex]?.x)}`;
    const yValue = `${yLabel}: ${formatYValue(ds?.data?.[dataIndex]?.y)}`;

    return (
      <>
        <Box
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: "black",
            maxWidth: 300,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="par01SemiBold" sx={{ fontSize: 15 }}>
            {ds?.label}
          </Typography>
          <Typography variant="par01Regular" sx={{ mt: 1, fontSize: 13 }}>
            {xValue}
          </Typography>
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {yValue}
          </Typography>
        </Box>
      </>
    );
  };

  const options = useMemo(() => {
    const result: Options = {
      maintainAspectRatio: false,
      animation: {
        duration: 500,
      },
      scales: {
        x: {
          offset: true,
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: formatXValue,
          },
          type: "linear",
          title: {
            text: xLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 16,
              weight: 600,
            },
          },
          grid: ChartUtils.getGridOptions({
            theme,
            showMinMaxLines: true,
          }),
        },
        y: {
          offset: true,
          type: "linear",
          title: {
            text: yLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 16,
              weight: 600,
            },
          },
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: formatYValue,
          },
          grid: ChartUtils.getGridOptions({ theme, showMinMaxLines: true }),
        },
      },
      interaction: {
        mode: "point",
        intersect: false,
      },
      plugins: {
        roundedBackground: ChartUtils.getRoundedBackgroundOptions({
          theme,
          isWhiteContext,
        }),
        tooltip: tooltipPlugin,
      },
    };
    return result;
  }, [theme, xLabel, yLabel, isWhiteContext, tooltipPlugin]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <ChartTooltip
        tooltipData={tooltipData}
        renderTooltip={renderTooltip}
        shiftY={0}
        sx={{
          /* Some bug: can't specify height on chart because it starts to grow, so put chart in the box */
          height: 480,
          position: "relative",
        }}
      >
        <Chart
          ref={chartRef}
          type="line"
          datasetIdKey="id"
          data={{ datasets }}
          options={options}
          plugins={[
            ChartPlugins.roundedBackground,
            ChartPlugins.rangeHighlight,
          ]}
        />
      </ChartTooltip>
      <CarChartLegend
        sx={{
          mt: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
        labelVariant="caption"
        items={datasets.map((ds) => ({
          label: ds.label ?? "",
          color: String(ds.borderColor),
        }))}
      />
    </Box>
  );
};
